<template>
  <div>
    <div class="w-full">
      <div class="mt-2 text-center">
        <div class="flex items-center justify-between gap-4 px-2 text-gray-900">
          <div class="flex flex-grow items-center justify-between">
            <button
              v-if="!restrictYearDate"
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 disabled:opacity-30"
              :disabled="isPreviousDateDisabled"
              @click="subtractYear"
            >
              <span class="sr-only">Previous year</span>
              <ChevronLeftIcon
                class="relative -right-3.5 h-5 w-5"
                aria-hidden="true"
              />
              <ChevronLeftIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <div
              v-else
              class="w-4"
            ></div>
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              :disabled="isPreviousDateDisabled"
              @click="subtractMonth"
            >
              <span class="sr-only">Previous month</span>
              <ChevronLeftIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
          </div>
          <div class="font-medium">{{ viewedMonthText }} {{ viewedYear }}</div>
          <div class="flex flex-grow items-center justify-between">
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              @click="addMonth"
            >
              <span class="sr-only">Next month</span>
              <ChevronRightIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="!restrictYearDate"
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 disabled:opacity-30"
              :disabled="restrictYearDate"
              @click="addYear"
            >
              <span class="sr-only">Next year</span>
              <ChevronRightIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
              <ChevronRightIcon
                class="relative -left-3.5 h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <div
              v-else
              class="w-4"
            ></div>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div
          class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
        >
          <button
            v-for="(day, dayIdx) in days"
            :key="day.date"
            type="button"
            :class="[
              'py-1.5 hover:bg-gray-100 focus:z-10',
              day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
              isDateAfterMaxDate(day.date)
                ? 'cursor-not-allowed opacity-60'
                : day.isSelected && 'text-white',
              !day.isSelected &&
                day.isCurrentMonth &&
                !day.isToday &&
                'text-gray-900',
              !day.isSelected &&
                !day.isCurrentMonth &&
                !day.isToday &&
                'text-gray-400',
              day.isToday && !day.isSelected && 'text-yellow-20',
              dayIdx === 0 && 'rounded-tl-lg',
              dayIdx === 6 && 'rounded-tr-lg',
              dayIdx === days.length - 7 && 'rounded-bl-lg',
              dayIdx === days.length - 1 && 'rounded-br-lg',
            ]"
            :disabled="isDateAfterMaxDate(day.date)"
            @click="selectDay(day, dayIdx)"
          >
            <time
              :datetime="day.date"
              :class="[
                'mx-auto flex h-7 w-7 items-center justify-center rounded-md',
                day.isSelected && day.isToday && 'bg-yellow-500',
                day.isSelected && !day.isToday && 'bg-yellow-500',
                day.isRangeSelected && 'bg-yellow-200 text-yellow-600',
              ]"
            >
              {{ day?.date.split('-').pop().replace(/^0/, '') }}
            </time>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/solid';
import { ref, toRef, computed, watch, onBeforeMount } from 'vue';
import dayjs from 'dayjs';

export default {
  name: 'DatePickerRange',
  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    field: String,
    startDate: {
      type: String,
    },
    endDate: {
      type: String,
    },
    dateFormat: {
      type: String,
      default: '',
      required: false,
    },
    maxDate: {
      type: String,
      required: false,
      default: undefined,
    },
    disablePrevious: {
      type: Boolean,
      required: false,
      default: false,
    },
    restrictPrevious: {
      type: Boolean,
      required: false,
      default: false,
    },
    restrictPreviousDate: {
      type: String,
      required: false,
      default: '',
    },
    restrictYearDate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const field = ref(props.field);
    const startDateValue = toRef(props, 'startDate');
    const endDateValue = toRef(props, 'endDate');
    const lastUpdatedDate = ref('start');
    const dateFormatValue = toRef(props, 'dateFormat');
    const maxDate = toRef(props, 'maxDate');

    if (dateFormatValue.value == '') {
      dateFormatValue.value = 'YYYY-MM-DD';
    }

    const viewedDate = ref(
      dayjs(startDateValue.value).startOf('month').format('YYYY-MM-DD')
    );

    const viewedMonth = computed(() => {
      return dayjs(viewedDate.value).format('M');
    });
    const viewedMonthText = computed(() => {
      return dayjs(viewedDate.value).format('MMMM');
    });
    const viewedYear = computed(() => {
      return dayjs(viewedDate.value).format('YYYY');
    });
    const startDateDay = computed(() => {
      return dayjs(startDateValue.value).format('D');
    });
    const endDateDay = computed(() => {
      return dayjs(endDateValue.value).format('D');
    });
    const startMonth = computed(() => {
      return dayjs(startDateValue.value).format('M');
    });
    const startYear = computed(() => {
      return dayjs(startDateValue.value).format('YYYY');
    });
    const endMonth = computed(() => {
      return dayjs(endDateValue.value).format('M');
    });
    const endYear = computed(() => {
      return dayjs(endDateValue.value).format('YYYY');
    });
    const days = ref([
      { date: '2021-12-27' },
      { date: '2021-12-28' },
      { date: '2021-12-29' },
      { date: '2021-12-30' },
      { date: '2021-12-31' },
      { date: '2022-01-01', isCurrentMonth: true },
      { date: '2022-01-02', isCurrentMonth: true },
      { date: '2022-01-03', isCurrentMonth: true },
      { date: '2022-01-04', isCurrentMonth: true },
      { date: '2022-01-05', isCurrentMonth: true },
      { date: '2022-01-06', isCurrentMonth: true },
      { date: '2022-01-07', isCurrentMonth: true },
      { date: '2022-01-08', isCurrentMonth: true },
      { date: '2022-01-09', isCurrentMonth: true },
      { date: '2022-01-10', isCurrentMonth: true },
      { date: '2022-01-11', isCurrentMonth: true },
      { date: '2022-01-12', isCurrentMonth: true },
      { date: '2022-01-13', isCurrentMonth: true },
      { date: '2022-01-14', isCurrentMonth: true },
      { date: '2022-01-15', isCurrentMonth: true },
      { date: '2022-01-16', isCurrentMonth: true },
      { date: '2022-01-17', isCurrentMonth: true },
      { date: '2022-01-18', isCurrentMonth: true },
      { date: '2022-01-19', isCurrentMonth: true },
      { date: '2022-01-20', isCurrentMonth: true },
      { date: '2022-01-21', isCurrentMonth: true },
      { date: '2022-01-22', isCurrentMonth: true },
      { date: '2022-01-23', isCurrentMonth: true },
      { date: '2022-01-24', isCurrentMonth: true },
      { date: '2022-01-25', isCurrentMonth: true },
      { date: '2022-01-26', isCurrentMonth: true },
      { date: '2022-01-27', isCurrentMonth: true },
      { date: '2022-01-28', isCurrentMonth: true },
      { date: '2022-01-29', isCurrentMonth: true },
      { date: '2022-01-30', isCurrentMonth: true },
      { date: '2022-01-31', isCurrentMonth: true },
      { date: '2022-02-01' },
      { date: '2022-02-02' },
      { date: '2022-02-03' },
      { date: '2022-02-04' },
      { date: '2022-02-05' },
      { date: '2022-02-06' },
    ]);

    function constructMonth() {
      let currentStart = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).format('YYYY-MM-DD');
      let daysInMonth = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).daysInMonth();
      let dayOfWeekFirst = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).format('d');
      let dayOfWeekLast = dayjs(
        viewedMonth.value + '/' + daysInMonth + '/' + viewedYear.value
      ).format('d');
      let dateLast = dayjs(
        viewedMonth.value + '/' + daysInMonth + '/' + viewedYear.value
      ).format('YYYY-MM-DD');
      // let dateLast = dayjs(viewedMonth.value+'/'+daysInMonth+'/'+viewedYear.value).format('YYYY-MM-DD');
      let calendarDays = [];
      for (let i = dayOfWeekFirst; i > 0; i--) {
        calendarDays.push({
          date: dayjs(currentStart).subtract(i, 'day').format('YYYY-MM-DD'),
          isDisabled: !!startDateDay.value,
        });
      }
      if (
        (startDateDay.value == 1 &&
          viewedMonth.value == startMonth.value &&
          viewedYear.value == startYear.value) ||
        (endDateDay.value == 1 &&
          viewedMonth.value == endMonth.value &&
          viewedYear.value == endYear.value)
      ) {
        calendarDays.push({
          date: dayjs(currentStart).format('YYYY-MM-DD'),
          isCurrentMonth: true,
          isSelected: true,
        });
      } else if (
        (viewedYear.value > startYear.value &&
          viewedYear.value < endYear.value) ||
        (viewedYear.value > startYear.value &&
          viewedYear.value == endYear.value &&
          viewedMonth.value < endMonth.value) ||
        (viewedYear.value < endYear.value &&
          viewedYear.value == startYear.value &&
          viewedMonth.value > startMonth.value) ||
        (startMonth.value !== endMonth.value &&
          ((viewedMonth.value > startMonth.value &&
            viewedMonth.value < endMonth.value) ||
            (viewedMonth.value == endMonth.value && endDateDay.value > 1) ||
            (viewedMonth.value < endMonth.value &&
              viewedMonth.value > startMonth.value)))
      ) {
        calendarDays.push({
          date: dayjs(currentStart).format('YYYY-MM-DD'),
          isCurrentMonth: true,
          isRangeSelected: true,
        });
      } else {
        const currentDay = dayjs().format('D');
        let isCurrentMonth = true;
        let isDisabled = false;

        if (props.restrictPrevious) {
          if (
            dayjs(currentStart).format('D') < currentDay &&
            dayjs().format('M') === viewedMonth.value &&
            dayjs().format('YYYY') === viewedYear?.value
          ) {
            isDisabled = true;
            isCurrentMonth = false;
          }
        } else {
          isDisabled = !!startDateDay.value;
        }

        calendarDays.push({
          date: dayjs(currentStart).format('YYYY-MM-DD'),
          isCurrentMonth,
          isDisabled,
        });
      }
      for (let i = 1; i < daysInMonth; i++) {
        // account for difference in years between viewed, start, and end dates
        if (
          (viewedYear.value > startYear.value &&
            viewedYear.value < endYear.value) ||
          (viewedYear.value > startYear.value &&
            viewedYear.value == endYear.value &&
            viewedMonth.value < endMonth.value) ||
          (viewedYear.value < endYear.value &&
            viewedYear.value == startYear.value &&
            viewedMonth.value > startMonth.value) ||
          (startMonth.value !== endMonth.value &&
            ((viewedMonth.value > startMonth.value &&
              viewedMonth.value < endMonth.value) ||
              (viewedMonth.value == endMonth.value && endDateDay.value > 1) ||
              (viewedMonth.value < endMonth.value &&
                viewedMonth.value > startMonth.value)))
        ) {
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            isRangeSelected: true,
          });
        } else if (
          (i + 1 == startDateDay.value &&
            startMonth.value == viewedMonth.value) ||
          (i + 1 == endDateDay.value &&
            endMonth.value == viewedMonth.value &&
            endDateValue.value)
        ) {
          let isDisabled = false;
          let isCurrentMonth = true;
          if (props?.restrictPreviousDate) {
            const isBefore = dayjs(currentStart)
              .add(i, 'day')
              .isBefore(props?.restrictPreviousDate);

            if (isBefore) {
              isDisabled = true;
              isCurrentMonth = false;
            }
          }

          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isDisabled,
            isCurrentMonth,
            isSelected: true,
          });
        } else if (
          viewedMonth.value == startMonth.value &&
          viewedMonth.value == endMonth.value &&
          i + 1 > startDateDay.value &&
          i + 1 < endDateDay.value
        ) {
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            isRangeSelected: true,
          });
        } else if (
          viewedMonth.value > startMonth.value &&
          viewedMonth.value == endMonth.value &&
          i + 1 < endDateDay.value
        ) {
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            isRangeSelected: true,
          });
        } else if (
          viewedMonth.value == startMonth.value &&
          viewedMonth.value < endMonth.value &&
          i + 1 > startDateDay.value
        ) {
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
          });
        } else if (
          viewedMonth.value > startMonth.value &&
          viewedMonth.value < endMonth.value
        ) {
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            isRangeSelected: true,
          });
        } else {
          const currentDay = dayjs().format('D');
          const currentMonth = dayjs().format('M');
          let isCurrentMonth = true;
          let isDisabled = false;
          if (props.restrictPrevious) {
            if (
              viewedMonth?.value === currentMonth &&
              i < currentDay &&
              dayjs().format('YYYY') === viewedYear?.value
            ) {
              isDisabled = true;
              isCurrentMonth = false;
            } else if (props?.restrictPreviousDate) {
              const isBefore = dayjs(currentStart)
                .add(i, 'day')
                .isBefore(props?.restrictPreviousDate);

              if (isBefore) {
                isDisabled = true;
                isCurrentMonth = false;
              }
            }

            if (i < startDateDay.value && currentMonth === viewedMonth.value) {
              isDisabled = true;
            }
          } else if (
            startDateValue.value &&
            viewedMonth.value <= startMonth.value &&
            i < startDateDay.value &&
            !endDateValue.value
          ) {
            isDisabled = true;
          }

          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth,
            isDisabled,
          });
        }
      }
      for (let i = 1; i <= 6 - dayOfWeekLast; i++) {
        calendarDays.push({
          date: dayjs(dateLast).add(i, 'day').format('YYYY-MM-DD'),
        });
      }
      days.value = [];
      days.value = calendarDays;
    }

    const selectedDay = ref({
      date: null,
      index: null,
    });
    function selectDay(day, dayIdx) {
      if (day.isDisabled) {
        return;
      }
      if (selectedDay.value.index) {
        if (days.value[selectedDay.value.index]) {
          days.value[selectedDay.value.index].isSelected = false;
        }

        if (days.value[dayIdx]) {
          days.value[dayIdx].isSelected = true;
        }
      } else {
        days.value.forEach(function (el) {
          el.isSelected = false;
        });
        days.value[dayIdx].isSelected = true;
      }
      selectedDay.value.index = dayIdx;
      selectedDay.value.date = days.value[dayIdx].date;
      let fieldValue = field.value;
      let dateSelected = selectedDay.value.date;
      dateSelected = dayjs(dateSelected).format(dateFormatValue.value);
      emit('daySelected', fieldValue, dateSelected);
    }

    onBeforeMount(() => {
      constructMonth();
    });

    watch(viewedDate, () => {
      constructMonth();
    });

    watch(
      () => props?.restrictPreviousDate,
      () => {
        constructMonth();
      }
    );

    watch(startDateValue, () => {
      lastUpdatedDate.value = 'start';
      viewedDate.value = dayjs(startDateValue.value)
        .startOf('month')
        .format('YYYY-MM-DD');
      constructMonth();
    });
    watch(endDateValue, () => {
      lastUpdatedDate.value = 'end';
      if (endDateValue.value) {
        viewedDate.value = dayjs(endDateValue.value)
          .startOf('month')
          .format('YYYY-MM-DD');
      }
      constructMonth();
    });

    function addMonth() {
      viewedDate.value = dayjs(viewedDate.value).add(1, 'month');
    }

    function subtractMonth() {
      viewedDate.value = dayjs(viewedDate.value).subtract(1, 'month');
    }

    function addYear() {
      viewedDate.value = dayjs(viewedDate.value).add(1, 'year');
    }

    function subtractYear() {
      const nextDate = dayjs(viewedDate.value)
        .subtract(1, 'year')
        .startOf('month');
      const currentDate = dayjs().startOf('month');

      const isBefore = nextDate.isBefore(currentDate);

      if (isBefore) {
        viewedDate.value = currentDate;
      } else {
        viewedDate.value = dayjs(viewedDate.value).subtract(1, 'year');
      }
    }

    const isDateAfterMaxDate = (date) => {
      if (!maxDate.value) return false; // If no MaxDate provided, assume no date is after it
      return dayjs(date).isAfter(dayjs(maxDate.value));
    };

    const isPreviousDateDisabled = computed(() => {
      if (props.disablePrevious) {
        const currentMonth = dayjs().format('M');
        const currentYear = dayjs().format('YYYY');

        const isOlder =
          viewedMonth?.value <= currentMonth &&
          viewedYear?.value <= currentYear;
        return isOlder;
      } else {
        return false;
      }
    });

    return {
      days,
      selectedDay,
      selectDay,
      viewedMonth,
      viewedMonthText,
      viewedYear,
      startDateValue,
      addMonth,
      subtractMonth,
      addYear,
      subtractYear,
      // daysInMonth,
      // calendarDays,
      startDateDay,
      isDateAfterMaxDate,
      endDateValue,
      endDateDay,
      startMonth,
      startYear,
      endMonth,
      endYear,
      isPreviousDateDisabled,
    };
  },
};
</script>
